import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, Sparkles, Mic, Hash, AtSign, Sun, Moon } from 'lucide-react';
import { Meeting } from '../types';
import { t } from '../lib/i18n';

interface OmnibarProps {
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  onStartRecord: () => void;
  onNavigateToChat: (initialPrompt?: string) => void;
  /**
   * Sohbet açıkken yanıt bekleniyor: yeni soru gönderilmez (yazılan metin kutuda kalır).
   * Aksi hâlde henüz kaydedilmemiş sohbette iki istek ayrı sohbetler açardı.
   */
  isChatBusy?: boolean;
  onToggleTheme: () => void;
  isDarkMode: boolean;
  isOpenModal?: boolean;
  onCloseModal?: () => void;
}

export const Omnibar: React.FC<OmnibarProps> = ({
  meetings,
  onSelectMeeting,
  onStartRecord,
  onNavigateToChat,
  isChatBusy = false,
  onToggleTheme,
  isDarkMode
}) => {
  const [query, setQuery] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'notes' | 'tools' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Detect @ and # trigger characters
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (val.endsWith('@')) {
      setPopupType('notes');
      setShowPopup(true);
    } else if (val.endsWith('#')) {
      setPopupType('tools');
      setShowPopup(true);
    } else if (!val.includes('@') && !val.includes('#')) {
      setShowPopup(false);
    }
  };

  const handleSelectNote = (m: Meeting) => {
    const newQuery = query.replace(/@\w*$/, `@${m.title} `);
    setQuery(newQuery);
    setShowPopup(false);
    inputRef.current?.focus();
  };

  const handleSelectTool = (toolId: string) => {
    setShowPopup(false);
    if (toolId === 'record') {
      setQuery('');
      onStartRecord();
    } else if (toolId === 'theme') {
      setQuery('');
      onToggleTheme();
    } else if (toolId === 'digest') {
      if (isChatBusy) return;
      setQuery('');
      onNavigateToChat(
        t(
          'Bugünün tüm toplantılarını ve kilit aksiyon maddelerini özetle',
          'Summarize today’s meetings and the key action items'
        )
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    // Önce mevcut bir toplantıyla eşleşiyor mu diye bak, eşleşmiyorsa AI
    // sohbetine yönlendir.
    //
    // Eski kural `query.includes(m.title)` idi: kısa başlıklı bir toplantı
    // ("Demo" gibi) neredeyse her soruyu kapıyor, kullanıcı AI'ya soru
    // soramıyordu. Artık yalnızca tam eşleşme, ya da tek bir toplantının
    // başlığı yazılanla başlıyorsa o toplantı açılıyor.
    const normalized = query.trim().toLocaleLowerCase('tr');
    const exact = meetings.find(m => m.title.toLocaleLowerCase('tr') === normalized);

    const prefixMatches = normalized.length >= 3
      ? meetings.filter(m => m.title.toLocaleLowerCase('tr').startsWith(normalized))
      : [];

    const foundMeeting = exact ?? (prefixMatches.length === 1 ? prefixMatches[0] : undefined);

    if (foundMeeting) {
      onSelectMeeting(foundMeeting);
    } else {
      if (isChatBusy) return;
      onNavigateToChat(query.trim());
    }
    setQuery('');
  };

  // Kısayollar: ⌘/Ctrl+K odaklanır, Escape açık listeyi kapatır.
  // Escape daha önce hiçbir şey yapmıyordu: @ ya da # yazıp fikrini
  // değiştiren kullanıcı, listeyi kapatmak için karakteri silmek zorundaydı.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (e.key === 'Escape' && showPopup) {
        setShowPopup(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPopup]);

  // Dışarı tıklamak da kapatır.
  useEffect(() => {
    if (!showPopup) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setShowPopup(false);
    };
    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [showPopup]);

  return (
    <div ref={rootRef} className="relative w-full max-w-2xl mx-auto select-none">
      {/* Contextual Popup Menu when typing @ or # */}
      {showPopup && (
        <div className={`absolute bottom-full mb-3 w-full p-3 rounded-2xl border shadow-2xl z-50 text-[13.5px] backdrop-blur-xl transition-all ${
          isDarkMode ? 'bg-[#19191C]/95 border-[#48484F] text-neutral-200 shadow-black/60' : 'bg-white/95 border-neutral-200 text-neutral-800 shadow-xl'
        }`}>
          {popupType === 'notes' && (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              <div className="px-2.5 py-1.5 text-[11px] uppercase font-bold text-neutral-500 tracking-[0.09em] flex items-center gap-2">
                <AtSign className="w-3.5 h-3.5 text-[#46CBE2]" />
                <span>{t('Bir Not veya Toplantı Ekle', 'Add a note or meeting')}</span>
              </div>
              {meetings.length === 0 && (
                <div className="px-3.5 py-2.5 text-[12.5px] text-neutral-500">
                  {t('Henüz eklenebilecek bir not yok.', 'No notes to add yet.')}
                </div>
              )}
              {meetings.slice(0, 8).map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleSelectNote(m)}
                  className={`w-full text-left px-3.5 py-2.5 rounded-xl flex items-center justify-between transition-colors ${
                    isDarkMode ? 'hover:bg-[#1F1F23] text-neutral-200' : 'hover:bg-neutral-100 text-neutral-800'
                  }`}
                >
                  <span className="font-semibold text-[13.5px] truncate">{m.title}</span>
                  <span className="text-[12px] text-neutral-400 font-mono">{m.date}</span>
                </button>
              ))}
            </div>
          )}

          {popupType === 'tools' && (
            <div className="space-y-1">
              <div className="px-2.5 py-1.5 text-[11px] uppercase font-bold text-neutral-500 tracking-[0.09em] flex items-center gap-2">
                <Hash className="w-3.5 h-3.5 text-amber-400" />
                <span>{t('Hızlı Araç Çalıştır', 'Run a quick tool')}</span>
              </div>
              <button
                onClick={() => handleSelectTool('record')}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl flex items-center gap-3 transition-colors ${
                  isDarkMode ? 'hover:bg-[#1F1F23] text-neutral-200' : 'hover:bg-neutral-100 text-neutral-800'
                }`}
              >
                <Mic className="w-4 h-4 text-rose-400" />
                <span>{t('#record — Yeni ses kaydı başlat', '#record — Start a new recording')}</span>
              </button>
              <button
                onClick={() => handleSelectTool('digest')}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl flex items-center gap-3 transition-colors ${
                  isDarkMode ? 'hover:bg-[#1F1F23] text-neutral-200' : 'hover:bg-neutral-100 text-neutral-800'
                }`}
              >
                <Sparkles className="w-4 h-4 text-[#46CBE2]" />
                <span>{t('#digest — Günlük toplantı özetini çıkar', '#digest — Get the daily meeting digest')}</span>
              </button>
              <button
                onClick={() => handleSelectTool('theme')}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl flex items-center gap-3 transition-colors ${
                  isDarkMode ? 'hover:bg-[#1F1F23] text-neutral-200' : 'hover:bg-neutral-100 text-neutral-800'
                }`}
              >
                {isDarkMode ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-purple-400" />}
                <span>{t('#theme — Temayı değiştir', '#theme — Switch the theme')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating Capsule Bar (Screenshot exact replica) */}
      <form 
        onSubmit={handleSubmit}
        className={`flex items-center gap-2.5 px-5 py-3 rounded-full border shadow-2xl backdrop-blur-2xl transition-all duration-200 ${
          isDarkMode 
            ? 'bg-[#19191C]/90 border-[#44444A] text-neutral-200 focus-within:border-[#3AC8E0] shadow-black/50' 
            : 'bg-white/90 border-neutral-300 text-neutral-900 focus-within:border-[#3AC8E0] shadow-xl'
        }`}
      >
        <Sparkles className="w-5 h-5 text-[#46CBE2] shrink-0" />
        <input
          id="omnibar-input"
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={t(
            'Bir şey sor ya da toplantı ara... @ not ekler · # araç çalıştırır',
            'Ask anything or search meetings… @ adds a note · # runs a tool'
          )}
          className={`flex-1 bg-transparent border-none outline-none text-[13.5px] min-w-0 placeholder:text-neutral-500 ${
            isDarkMode ? 'text-neutral-100' : 'text-neutral-900'
          }`}
        />

        {/* Action arrow button */}
        <button
          type="submit"
          disabled={!query.trim()}
          className="w-7 h-7 rounded-full bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:opacity-40 text-[#F0EEE6] flex items-center justify-center transition-all shadow-xs shrink-0 active:scale-95"
          title={t('Gönder', 'Send')}
        >
          <ArrowUp className="w-4 h-4 stroke-[2.5]" />
        </button>
      </form>
    </div>
  );
};
